import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { availableLanguages, LanguageCode, countryCodeToLanguageCode, languageCodeToCountryCode } from '@/config/i18n';
import i18n, { initializeI18n } from '@/config/i18n';

// 기본 언어 상태
const defaultState: LanguageStoreState = {
    currentLanguage: 'ko' as LanguageCode,
    availableLanguages: availableLanguages,
    isLanguageDetected: false,
};

// 언어 스토어 생성
export const useLanguageStore = create<LanguageStoreState & LanguageStoreAction>()(
    persist(
        (set, get) => ({
            ...defaultState,
            action: {
                // 언어 설정
                setLanguage: (language: LanguageCode) => {
                    const currentState = get();

                    // 현재 언어와 동일하면 아무것도 하지 않음
                    if (currentState.currentLanguage === language) {
                        return;
                    }

                    // 스토어 상태 업데이트 (persist로 localStorage에 저장됨)
                    set((state) => ({
                        ...state,
                        currentLanguage: language,
                        isLanguageDetected: true,
                    }));

                    // 즉시 페이지 새로고침 (i18n 변경 전에 새로고침하여 일관성 유지)
                    window.location.reload();
                },

                // CountryCode로 언어 설정 (CountrySelector 연동용)
                setLanguageByCountryCode: (countryCode: string) => {
                    const languageCode = countryCodeToLanguageCode[countryCode];
                    if (languageCode) {
                        get().action.setLanguage(languageCode);
                    }
                },

                // 브라우저 언어 감지 및 설정
                detectAndSetLanguage: () => {
                    const state = get();
                    if (state.isLanguageDetected) return;

                    // 이미 persist 미들웨어가 localStorage에서 복원했으므로
                    // currentLanguage가 기본값이 아닌 경우 저장된 언어를 사용
                    if (state.currentLanguage !== 'ko') {
                        // i18n도 동일한 언어로 설정
                        i18n.changeLanguage(state.currentLanguage);
                        set((prevState) => ({
                            ...prevState,
                            isLanguageDetected: true,
                        }));
                        return;
                    }

                    // localStorage에 저장된 언어가 없는 경우 브라우저 언어 감지
                    const browserLang = navigator.language.toLowerCase();

                    // 정확한 매칭 시도
                    if (Object.keys(availableLanguages).includes(browserLang)) {
                        get().action.setLanguage(browserLang as LanguageCode);
                        return;
                    }

                    // 언어 코드만으로 매칭
                    const langCode = browserLang.split('-')[0];
                    if (Object.keys(availableLanguages).includes(langCode)) {
                        get().action.setLanguage(langCode as LanguageCode);
                        return;
                    }

                    // 중국어 특별 처리
                    if (langCode === 'zh') {
                        if (browserLang.includes('tw') || browserLang.includes('hk')) {
                            get().action.setLanguage('zh-TW');
                        } else {
                            get().action.setLanguage('zh-CN');
                        }
                        return;
                    }

                    // 기본 언어 설정
                    get().action.setLanguage('ko');
                },

                // 언어 초기화
                resetLanguage: () => {
                    set((state) => ({
                        ...state,
                        currentLanguage: 'ko',
                        isLanguageDetected: false,
                    }));
                    i18n.changeLanguage('ko');
                },

                // 사용 가능한 언어 목록 가져오기
                getAvailableLanguages: () => {
                    return get().availableLanguages;
                },

                // 현재 언어 가져오기
                getCurrentLanguage: () => {
                    return get().currentLanguage;
                },

                // 현재 언어의 CountryCode 가져오기
                getCurrentCountryCode: () => {
                    const currentLanguage = get().currentLanguage;
                    return languageCodeToCountryCode[currentLanguage] || 'KR';
                },

                // 언어 감지 상태 가져오기
                getLanguageDetectionStatus: () => {
                    return get().isLanguageDetected;
                },

                // 언어가 지원되는지 확인
                isLanguageSupported: (language: string) => {
                    return Object.keys(get().availableLanguages).includes(language);
                },

                // 언어 코드를 언어명으로 변환
                getLanguageName: (languageCode: LanguageCode) => {
                    return get().availableLanguages[languageCode];
                },

                // 현재 언어명 가져오기
                getCurrentLanguageName: () => {
                    const state = get();
                    return state.availableLanguages[state.currentLanguage];
                },
            },
        }),
        {
            name: 'language-storage',
            storage: createJSONStorage(() => localStorage),
            // 저장할 상태만 선택
            partialize: (state) => ({
                currentLanguage: state.currentLanguage,
                isLanguageDetected: state.isLanguageDetected,
            }),
        },
    ),
);

// 언어 상태 인터페이스
export interface LanguageStoreState {
    currentLanguage: LanguageCode;
    availableLanguages: typeof availableLanguages;
    isLanguageDetected: boolean;
}

// 언어 액션 인터페이스
export interface LanguageStoreAction {
    action: {
        setLanguage: (language: LanguageCode) => void;
        setLanguageByCountryCode: (countryCode: string) => void;
        detectAndSetLanguage: () => void;
        resetLanguage: () => void;
        getAvailableLanguages: () => typeof availableLanguages;
        getCurrentLanguage: () => LanguageCode;
        getCurrentCountryCode: () => string;
        getLanguageDetectionStatus: () => boolean;
        isLanguageSupported: (language: string) => boolean;
        getLanguageName: (languageCode: LanguageCode) => string;
        getCurrentLanguageName: () => string;
    };
}

// 언어 스토어 초기화 함수
export const initializeLanguageStore = () => {
    // localStorage에서 저장된 언어 확인
    const storedLanguage = localStorage.getItem('language-storage');
    let savedLanguage: LanguageCode | null = null;

    if (storedLanguage) {
        try {
            const parsed = JSON.parse(storedLanguage);
            const currentLanguage = parsed.state?.currentLanguage || parsed.currentLanguage;
            if (currentLanguage && Object.keys(availableLanguages).includes(currentLanguage)) {
                savedLanguage = currentLanguage as LanguageCode;
                console.log('Found saved language in localStorage:', savedLanguage);
            }
        } catch (e) {
            console.warn('Failed to parse stored language:', e);
        }
    }

    // i18n을 저장된 언어로 초기화
    if (savedLanguage) {
        initializeI18n(savedLanguage);
        // 스토어 상태도 업데이트
        const { setLanguage } = useLanguageStore.getState().action;
        setLanguage(savedLanguage);
    } else {
        // 저장된 언어가 없으면 브라우저 언어 감지
        const { detectAndSetLanguage } = useLanguageStore.getState().action;
        detectAndSetLanguage();
    }
};

// 언어 전환 훅 (편의 함수)
export const useLanguage = () => {
    const { currentLanguage, availableLanguages, isLanguageDetected } = useLanguageStore();
    const {
        setLanguage,
        setLanguageByCountryCode,
        detectAndSetLanguage,
        resetLanguage,
        getCurrentCountryCode,
        isLanguageSupported,
        getLanguageName,
        getCurrentLanguageName,
    } = useLanguageStore((state) => state.action);

    return {
        currentLanguage,
        availableLanguages,
        isLanguageDetected,
        setLanguage,
        setLanguageByCountryCode,
        detectAndSetLanguage,
        resetLanguage,
        getCurrentCountryCode,
        isLanguageSupported,
        getLanguageName,
        getCurrentLanguageName,
    };
};
